import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { X, Trophy, ShareNetwork, Check, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtCents } from "@/pages/dashboard/util";

const TEAL = ["#2DD4BF", "#00E676", "#0E9E90", "#67E8F9", "#ffffff"];

function fireConfetti() {
  const end = Date.now() + 1400;
  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 70, origin: { x: 0 }, colors: TEAL, scalar: 0.9 });
    confetti({ particleCount: 5, angle: 120, spread: 70, origin: { x: 1 }, colors: TEAL, scalar: 0.9 });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 120, spread: 100, origin: { y: 0.35 }, colors: TEAL });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/* Draw a branded 1200x630 social badge PNG on a canvas */
function drawBadge(rank, bonusCents, name, refLink) {
  const W = 1200, H = 630;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");

  // base
  ctx.fillStyle = "#05060A"; ctx.fillRect(0, 0, W, H);
  // teal glow top-right
  let g = ctx.createRadialGradient(W - 120, 120, 40, W - 120, 120, 620);
  g.addColorStop(0, "rgba(45,212,191,0.30)"); g.addColorStop(1, "rgba(5,6,10,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  // green glow bottom-left
  g = ctx.createRadialGradient(80, H - 80, 20, 80, H - 80, 520);
  g.addColorStop(0, "rgba(0,230,118,0.14)"); g.addColorStop(1, "rgba(5,6,10,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  // border
  ctx.strokeStyle = "rgba(45,212,191,0.45)"; ctx.lineWidth = 3;
  roundRect(ctx, 24, 24, W - 48, H - 48, 28); ctx.stroke();

  // brand
  ctx.fillStyle = "#2DD4BF"; ctx.beginPath(); ctx.arc(72, 84, 9, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#E2E8F0"; ctx.font = "700 26px Inter, Arial, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText("QUANTUM SCALP AI", 94, 86);
  ctx.fillStyle = "#64748B"; ctx.font = "600 18px Inter, Arial, sans-serif";
  ctx.textAlign = "right"; ctx.fillText("QUANTUM FLEX", W - 72, 86); ctx.textAlign = "left";

  // trophy
  ctx.font = "120px serif"; ctx.textAlign = "center"; ctx.fillText("🏆", W / 2, 210);

  // rank unlocked label
  ctx.fillStyle = "#2DD4BF"; ctx.font = "700 24px Inter, Arial, sans-serif";
  ctx.fillText("R A N K   U N L O C K E D", W / 2, 300);
  // rank name
  ctx.fillStyle = "#FFFFFF"; ctx.font = "800 88px Inter, Arial, sans-serif";
  ctx.fillText(rank.name, W / 2, 372);
  ctx.fillStyle = "#94A3B8"; ctx.font = "500 24px Inter, Arial, sans-serif";
  ctx.fillText(`Rank ${rank.level} of 12 · Quantum Flex ladder`, W / 2, 428);

  // bonus pill
  if (bonusCents > 0) {
    const label = `+${fmtCents(bonusCents)} RANK BONUS`;
    ctx.font = "700 30px Inter, Arial, sans-serif";
    const tw = ctx.measureText(label).width;
    const pw = tw + 64, ph = 62, px = (W - pw) / 2, py = 460;
    ctx.fillStyle = "rgba(0,230,118,0.12)"; roundRect(ctx, px, py, pw, ph, 31); ctx.fill();
    ctx.strokeStyle = "rgba(0,230,118,0.5)"; ctx.lineWidth = 2; roundRect(ctx, px, py, pw, ph, 31); ctx.stroke();
    ctx.fillStyle = "#00E676"; ctx.fillText(label, W / 2, py + ph / 2 + 1);
  }

  // footer: name + referral link
  ctx.textAlign = "left"; ctx.fillStyle = "#CBD5E1"; ctx.font = "600 22px Inter, Arial, sans-serif";
  ctx.fillText(name || "Quantum Member", 72, H - 60);
  ctx.textAlign = "right"; ctx.fillStyle = "#2DD4BF"; ctx.font = "500 20px Inter, Arial, sans-serif";
  ctx.fillText(`Join: ${refLink}`, W - 72, H - 60);
  ctx.textAlign = "left";

  return c;
}

export default function RankCelebration() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [meta, setMeta] = useState({ name: "", refLink: "" });

  useEffect(() => {
    let on = true;
    const check = () => api.get("/quantum-flex/rank-celebration")
      .then((r) => { if (on && r.data?.pending) setData(r.data); })
      .catch(() => {});
    check();
    const t = setInterval(check, 30000); // catch rank-ups from downline sales while browsing
    return () => { on = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!data) return;
    fireConfetti();
    const t = setTimeout(fireConfetti, 700);
    // pull name + referral link for the badge
    Promise.all([
      api.get("/auth/me").then((r) => r.data).catch(() => null),
      api.get("/referrals/me").then((r) => r.data).catch(() => null),
    ]).then(([me, ref]) => {
      const name = me?.full_name || me?.name || (me?.email ? me.email.split("@")[0] : "Quantum Member");
      const code = ref?.code || ref?.referral_code || "";
      const refLink = code ? `${window.location.host}/register?ref=${code}` : window.location.host;
      setMeta({ name, refLink });
    });
    return () => clearTimeout(t);
  }, [data]);

  const close = useCallback(async () => {
    try { await api.post("/quantum-flex/rank-celebration/ack"); } catch (e) { /* noop */ }
    setData(null);
  }, []);

  const rk = data?.rank;
  const shareText = rk
    ? `🏆 I just reached ${rk.name} (Rank ${rk.level}) on Quantum Scalp AI${data.bonus_cents ? ` and earned a ${fmtCents(data.bonus_cents)} rank bonus` : ""}! #QuantumScalp #QuantumFlex`
    : "";

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Quantum Scalp AI", text: shareText }); return; } catch (e) { /* fallthrough */ }
    }
    try { await navigator.clipboard.writeText(shareText); } catch (e) { /* clipboard blocked */ }
    setCopied(true); setTimeout(() => setCopied(false), 2200);
    toast.success("Milestone copied — paste it anywhere to share!");
  };

  const badge = async () => {
    try {
      const canvas = drawBadge(rk, data.bonus_cents, meta.name, meta.refLink);
      const fileName = `quantum-scalp-${rk.name.toLowerCase().replace(/\s+/g, "-")}-badge.png`;
      canvas.toBlob(async (blob) => {
        if (!blob) { toast.error("Could not generate the badge."); return; }
        const file = new File([blob], fileName, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file], text: shareText, title: "Quantum Scalp AI" }); return; }
          catch (e) { /* fall back to download */ }
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = fileName; document.body.appendChild(a); a.click();
        a.remove(); URL.revokeObjectURL(url);
        toast.success("Rank badge downloaded — share it anywhere!");
      }, "image/png");
    } catch (e) {
      toast.error("Could not generate the badge.");
    }
  };

  return (
    <AnimatePresence>
      {data && rk && (
        <motion.div
          className="fixed inset-0 z-[80] grid place-items-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="rank-celebration">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ scale: 0.8, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="relative w-full max-w-md rounded-2xl border border-[#2DD4BF]/30 bg-[#0A0B10] overflow-hidden qs-glow-blue">
            <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-[#2DD4BF]/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-[#00E676]/10 blur-3xl" />
            <button onClick={close} data-testid="celebration-close" className="absolute top-4 right-4 z-10 text-slate-400 hover:text-white transition-colors"><X size={20} /></button>

            <div className="relative p-8 text-center">
              <motion.div
                initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                className="mx-auto mb-5 grid place-items-center h-24 w-24 rounded-2xl bg-gradient-to-br from-[#2DD4BF] to-[#0E9E90] shadow-lg shadow-[#2DD4BF]/30">
                <Trophy size={48} weight="fill" className="text-[#030305]" />
              </motion.div>

              <p className="text-[10px] uppercase font-mono tracking-[0.3em] text-[#2DD4BF] mb-1">Rank Unlocked</p>
              <h2 className="text-3xl font-extrabold text-white mb-1" data-testid="celebration-rank-name">{rk.name}</h2>
              <p className="text-sm text-[#94A3B8] mb-5">You've climbed to Rank {rk.level} of the Quantum Flex ladder.</p>

              {data.bonus_cents > 0 && (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.35 }}
                  className="mb-6 rounded-xl border border-[#00E676]/25 bg-[#00E676]/[0.06] p-4">
                  <p className="text-[10px] uppercase font-mono text-[#64748B]">Rank bonus credited</p>
                  <p className="text-3xl font-bold font-mono text-[#00E676] mt-1" data-testid="celebration-bonus">+{fmtCents(data.bonus_cents)}</p>
                  <p className="text-[11px] text-[#94A3B8] mt-1">Added to your Referral wallet</p>
                </motion.div>
              )}

              <div className="space-y-2">
                <button onClick={badge} data-testid="celebration-badge"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white transition-colors">
                  <DownloadSimple size={16} weight="bold" /> Download shareable badge
                </button>
                <div className="flex gap-2">
                  <button onClick={share} data-testid="celebration-share"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#2DD4BF]/40 text-[#2DD4BF] text-sm font-semibold hover:bg-[#2DD4BF]/10 transition-colors">
                    {copied ? <><Check size={16} /> Copied!</> : <><ShareNetwork size={16} weight="bold" /> Share text</>}
                  </button>
                  <button onClick={close} data-testid="celebration-continue"
                    className="px-5 py-3 rounded-full border border-white/15 text-white text-sm hover:bg-white/5 transition-colors">Continue</button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
